// For google maps api
import { Controller } from "@hotwired/stimulus"
import * as myMap from "./maps";
import { defaultMapOptions, resolveMapTypeId, zoomToShape, layerManager } from "./maps";
import { stopLinkBeingFollowed } from "./maps";
import { MOUSEOVER_SHAPE_STYLES, SHAPE_STYLES } from "./style_defaults";
import { mapInfoPanel } from "./map_info_panel";

// for flight entry and editing
// polygon is stored in global variable (this.polygon) and the dom as a polyPath (this.polygonTarget.value)
// also stored is the drawingManager (this.drawingManager) and map (this.map)
// lessons learned:
// add the eventlisteners in this file (and use _this) to avoid scope problems with 'this'
export default class extends Controller {
  static targets = ['map', 'polygon', 'circleCentre', 'circleRadius', 'multipleShapes', 'toggleRestrictedAirspace', 'toggleCraneZones', 'safeguardingVisible', 'aazs', 'infoPanel', 'infoPanelContent'];
  static values = {
    applicationType: {type: String, default: 'flightApplication'}
  }

  ////////////////////// display overlays

  // Use centralized toggle method
  async toggleLayer(event) {
    await layerManager.handleToggleLayer(event);
  }
  ////////////////////// multiple shapes for WIP applications

  get isWIPApplication() {
    return this.applicationTypeValue === 'wipApplication';
  }

  get useMultipleShapes() {
    return this.isWIPApplication && this.hasMultipleShapesTarget;
  }

  loadExistingMultipleShapes() {
    const multipleShapesData = this.multipleShapesTarget.value;
    const locked = (this.data.get("locked") === 'true');

    if (multipleShapesData && multipleShapesData.trim() !== '') {
      try {
        const shapes = JSON.parse(multipleShapesData);

        if (shapes && shapes.length > 0) {
          shapes.forEach((shapeData, index) => {
            this.addShapeToMap(shapeData, index, locked);
          });
          this.updateShapesList();
        }
      } catch (error) {
        console.error('Error parsing multiple shapes data:', error);
      }
    }
  }

  addShapeToMap(shapeData, index, locked = false) {
    let mapShape;

    try {
      if (shapeData.type === 'polygon') {
        mapShape = myMap.drawPolygonFromPath(this.map, shapeData.data);
        if (!locked) {
          mapShape.setOptions({ editable: true, draggable: true });
          this.addListenersToPolygon(mapShape);
        } else {
          this.addClickForwardingListener(mapShape);
        }
      } else if (shapeData.type === 'circle') {
        mapShape = myMap.drawCircleFromPath(this.map, shapeData.data.center, shapeData.data.radius);
        if (!locked) {
          mapShape.setOptions({ editable: true, draggable: true });
          this.addListenersToCircle(mapShape);
        } else {
          this.addClickForwardingListener(mapShape);
        }
      }

      if (mapShape) {
        const shape = {
          type: shapeData.type,
          data: shapeData.data,
          mapObject: mapShape,
          index: index
        };
        this.shapes.push(shape);
      } else {
        console.error('Failed to create map shape');
      }
    } catch (error) {
      console.error('Error adding shape to map:', error);
    }
  }

  addShapeToCollection(type, data, mapObject) {
    const shape = {
      type: type,
      data: data,
      mapObject: mapObject,
      index: this.shapes.length
    };
    this.shapes.push(shape);
    this.updateMultipleShapesField();
    this.updateShapesList();
  }

  removeShapeFromCollection(index) {
    if (this.shapes[index]) {
      this.shapes[index].mapObject.setMap(null); // detach from map
      this.shapes.splice(index, 1);
      // Update indices
      this.shapes.forEach((shape, i) => {
        shape.index = i;
      });
      this.updateMultipleShapesField();
      this.updateShapesList();
    }
  }

  updateMultipleShapesField() {
    const shapesData = this.shapes.map(shape => ({
      type: shape.type,
      data: shape.data
    }));
    this.multipleShapesTarget.value = JSON.stringify(shapesData);
  }

  updateShapesList() {
    const count = this.shapes.length;
    const locked = (this.data.get("locked") === 'true');
    const downloadUrl = this.data.get("download-url");

    let html = ``;
    if (count > 0) {
      html += '<div class="shapes-list">';
      this.shapes.forEach((shape, index) => {
        html += `<div class="shape-item">
            <strong class="shape-name-hover"
                    data-shape-index="${index}"
                    data-action="mouseenter->form-map#highlightShape mouseleave->form-map#unhighlightShape"
                    style="cursor: pointer;">${shape.type.charAt(0).toUpperCase() + shape.type.slice(1)} ${index + 1}</strong>`
            if (!locked) {
              html += `<button type="button" class="btn btn-sm btn-danger" data-action="form-map#removeShape" data-shape-index="${index}">Delete</button>`
            } else if (downloadUrl) {
              html += `<a href="${downloadUrl}?shape_index=${index}" class="btn btn-sm btn-outline-secondary" data-turbo="false">Download KML</a>`
            }
        html += `</div>`;
          // To show coordinates, add this before the final div: ${this.generateCoordinatesDisplay(shape)}
      });
      html += '</div>'; // shapes-list
    } else {
      html += '<p>No shapes drawn</p>';
    }

    const coordinatesTable = document.getElementById('coordinates-table');
    if (coordinatesTable) {
      coordinatesTable.innerHTML = html;
    } else {
      console.error('Could not find coordinates-table element');
    }
  }

  updateSingleShapeDisplay(shapeType, data, radius = null) {
    const shape = { type: shapeType, data: data };
    if (radius) shape.radius = radius;

    const locked = (this.data.get("locked") === 'true');
    const downloadUrl = this.data.get("download-url");

    let html = `<div class="shape-item">
            <div class="d-flex align-items-center gap-2 mb-1">
                <strong class="shape-name-hover"
                        data-action="mouseenter->form-map#highlightSingleShape mouseleave->form-map#unhighlightSingleShape"
                        style="cursor: pointer;">${shapeType.charAt(0).toUpperCase() + shapeType.slice(1)}</strong>`;

    if (!locked) {
      html += `<button type="button" class="btn btn-sm btn-danger py-0 px-1" data-action="form-map#deleteShape" data-turbo="false">Delete</button>`;
    } else if (downloadUrl) {
      html += `<a href="${downloadUrl}" class="btn btn-sm btn-outline-secondary py-0 px-1" data-turbo="false">Download KML</a>`;
    }

    html += `</div>${this.generateCoordinatesDisplay(shape)}</div>`;

    const coordinatesTable = document.getElementById('coordinates-table');
    if (coordinatesTable) {
      coordinatesTable.innerHTML = html;
    } else {
      console.error('Could not find coordinates-table element');
    }
  }

  clearCoordinatesDisplay() {
    const coordinatesTable = document.getElementById('coordinates-table');
    if (coordinatesTable) {
      coordinatesTable.innerHTML = '<p>None</p>';
    } else {
      console.error('Could not find coordinates-table element');
    }
  }

  generateCoordinatesDisplay(shape) {
    if (shape.type === 'polygon') {
      return this.generatePolygonCoordinatesDisplay(shape.data);
    } else if (shape.type === 'circle') {
      return this.generateCircleCoordinatesDisplay(shape.data);
    }
    return '';
  }

  generatePolygonCoordinatesDisplay(encodedPath) {
    try {
      const data = google.maps.geometry.encoding.decodePath(encodedPath);
      let html = '<table class="table table-sm table-striped" style="font-size: 0.8em;">';
      html += '<thead><tr><th>Latitude</th><th>Longitude</th></tr></thead>';
      html += '<tbody>';

      for (let i = 0; i < data.length; i++) {
        const item = data[i].toJSON();
        html += `<tr>
          <td>${this.convertToDms(item.lat)}</td>
          <td>${this.convertToDms(item.lng, true)}</td>
        </tr>`;
      }
      html += '</tbody></table>';
      return html;
    } catch (error) {
      console.error('Error generating polygon coordinates:', error);
      return '<p>Error displaying coordinates</p>';
    }
  }

  generateCircleCoordinatesDisplay(circleData) {
    try {
      const centre = google.maps.geometry.encoding.decodePath(circleData.center)[0];
      const item = centre.toJSON();
      let html = '<table class="table table-sm table-striped" style="font-size: 0.8em;">';
      html += '<tbody>';
      html += `<tr><td><strong>Centre Lat</strong></td><td>${this.convertToDms(item.lat)}</td></tr>`;
      html += `<tr><td><strong>Centre Lon</strong></td><td>${this.convertToDms(item.lng, true)}</td></tr>`;
      html += `<tr><td><strong>Radius</strong></td><td>${Math.round(circleData.radius)}m</td></tr>`;
      html += '</tbody></table>';
      return html;
    } catch (error) {
      console.error('Error generating circle coordinates:', error);
      return '<p>Error displaying coordinates</p>';
    }
  }

  convertToDms(dd, isLng = false) {
    const dir = dd < 0
      ? isLng ? 'W' : 'S'
      : isLng ? 'E' : 'N';
    const absDd = Math.abs(dd);
    const deg = absDd | 0;
    const frac = absDd - deg;
    const min = (frac * 60) | 0;
    const sec = frac * 3600 - min * 60;
    // Round it to 2 decimal points.
    const roundedSec = Math.round(sec * 100) / 100;
    return deg + "°" + min.toString().padStart(2, '0') + "'" + roundedSec.toString().padStart(2, '0') + '"' + dir;
  }

  ////////////////////// polygons

  polygonComplete(polygon_listener) {
    // console.log('polygon complete');

    if (this.useMultipleShapes) {
      // For WIP applications, add to collection
      let path = google.maps.geometry.encoding.encodePath(polygon_listener.getPath());
      this.addShapeToCollection('polygon', path, polygon_listener);
      this.addListenersToPolygon(polygon_listener);
      // Keep drawing manager visible for more shapes
      myMap.showDrawingManager(this.drawingManager);
    } else {
      // Original single shape behaviour
      this.polygon = polygon_listener;
      let path = google.maps.geometry.encoding.encodePath(this.polygon.getPath());
      this.polygonTarget.value = path;
      myMap.hideDrawingManager(this.drawingManager);
      this.updateSingleShapeDisplay('polygon', path);
      this.addListenersToPolygon(this.polygon);
      this.deleteCircle(); // double check!
    }
  }

  deletePolygon() {
    this.polygon = null;
    this.polygonTarget.value = '';
  }

  polygonChanged(polygon_listener) {
    // console.log('polygon changed');

    if (this.useMultipleShapes) {
      // For WIP applications, update the shape in the collection
      const shapeIndex = this.shapes.findIndex(shape => shape.mapObject === polygon_listener);
      if (shapeIndex !== -1) {
        const path = google.maps.geometry.encoding.encodePath(polygon_listener.getPath());
        this.shapes[shapeIndex].data = path;
        this.updateMultipleShapesField();
        this.updateShapesList();
      }
    } else {
      // Original single shape behavior
      this.polygon = polygon_listener;
      this.polygonTarget.value = google.maps.geometry.encoding.encodePath(this.polygon.getPath());
      let path = google.maps.geometry.encoding.encodePath(this.polygon.getPath());
      this.updateSingleShapeDisplay('polygon', path);
    }
  }

  addListenersToPolygon(polygon){
    var _this = this;
    google.maps.event.addListener(polygon.getPath(), 'set_at', function(){_this.polygonChanged(polygon)});
    google.maps.event.addListener(polygon.getPath(), 'insert_at', function(){_this.polygonChanged(polygon)});
    google.maps.event.addListener(polygon.getPath(), 'remove_at', function(){_this.polygonChanged(polygon)});
    google.maps.event.addListener(polygon.getPath(), 'dragend', function(){_this.polygonChanged(polygon)});
    // Allow clicks on user's polygon to trigger info panel for layers underneath
    google.maps.event.addListener(polygon, 'click', function(event){
      layerManager.handleShapeClick(event.latLng);
    });
  }

  ////////////////////// circles

  circleComplete(circle_listener) {
    // console.log('circle complete');

    if (this.useMultipleShapes) {
      // For WIP applications, add to collection
      const center = google.maps.geometry.encoding.encodePath([circle_listener.center]);
      const radius = circle_listener.radius;
      const circleData = { center: center, radius: radius };
      this.addShapeToCollection('circle', circleData, circle_listener);
      this.addListenersToCircle(circle_listener);
      // Keep drawing manager visible for more shapes
      // myMap.showDrawingManager(this.drawingManager);
    } else {
      // Original single shape behavior
      this.circle = circle_listener;
      this.circleCentreTarget.value = google.maps.geometry.encoding.encodePath([this.circle.center]);
      this.circleRadiusTarget.value = this.circle.radius;
      myMap.hideDrawingManager(this.drawingManager);
      this.updateSingleShapeDisplay('circle', { center: this.circleCentreTarget.value, radius: this.circle.radius });
      this.addListenersToCircle(this.circle);
      this.deletePolygon(); // double check!
    }
  }

  deleteCircle() {
    this.circle = null;
    this.circleCentreTarget.value = '';
    this.circleRadiusTarget.value = '';
  }

  circleChanged(circle_listener) {
    // console.log('circle changed');

    if (this.useMultipleShapes) {
      // For WIP applications, update the shape in the collection
      const shapeIndex = this.shapes.findIndex(shape => shape.mapObject === circle_listener);
      if (shapeIndex !== -1) {
        const center = google.maps.geometry.encoding.encodePath([circle_listener.center]);
        const radius = circle_listener.radius;
        this.shapes[shapeIndex].data = { center: center, radius: radius };
        this.updateMultipleShapesField();
        this.updateShapesList();
      }
    } else {
      // Original single shape behavior
      this.circle = circle_listener;
      this.circleCentreTarget.value = google.maps.geometry.encoding.encodePath([this.circle.center]);
      this.circleRadiusTarget.value = this.circle.radius;
      this.updateSingleShapeDisplay('circle', { center: this.circleCentreTarget.value, radius: this.circle.radius });
    }
  }

  addListenersToCircle(circle){
    var _this = this;
    google.maps.event.addListener(circle, 'radius_changed', function(){_this.circleChanged(circle)});
    google.maps.event.addListener(circle, 'center_changed', function(){_this.circleChanged(circle)});
    // Allow clicks on user's circle to trigger info panel for layers underneath
    google.maps.event.addListener(circle, 'click', function(event){
      layerManager.handleShapeClick(event.latLng);
    });
  }

  // Forward clicks on locked (read-only) shapes to the layer manager info panel
  addClickForwardingListener(shape) {
    google.maps.event.addListener(shape, 'click', function(event) {
      layerManager.handleShapeClick(event.latLng);
    });
  }

  ////////////////////// shape highlighting

  highlightShape(event) {
    const shapeIndex = parseInt(event.target.dataset.shapeIndex);
    if (!isNaN(shapeIndex) && this.shapes[shapeIndex]) {
      const shape = this.shapes[shapeIndex];
      if (shape.mapObject) {
        // Apply highlight style
        shape.mapObject.setOptions({
          ...MOUSEOVER_SHAPE_STYLES
        });
      }
    }
  }

  unhighlightShape(event) {
    const shapeIndex = parseInt(event.target.dataset.shapeIndex);
    if (!isNaN(shapeIndex) && this.shapes[shapeIndex]) {
      const shape = this.shapes[shapeIndex];
      if (shape.mapObject) {
        // Restore original style
        shape.mapObject.setOptions({
          ...SHAPE_STYLES
        });
      }
    }
  }

  highlightSingleShape(event) {
    const shape = this.polygon || this.circle;
    if (shape) {
      // Apply highlight style
      shape.setOptions({
        ...MOUSEOVER_SHAPE_STYLES
      });
    }
  }

  unhighlightSingleShape(event) {
    const shape = this.polygon || this.circle;
    if (shape) {
      // Restore original style
      shape.setOptions({
        ...SHAPE_STYLES
      });
    }
  }

  ////////////////////// other stuff

  addListenersToDrawingManager(dm) {
    var _this = this;
    google.maps.event.addListener(dm, 'polygoncomplete', function (polygon_listener) {
      _this.polygonComplete(polygon_listener);
    });
    google.maps.event.addListener(dm, 'circlecomplete', function (circle_listener) {
      _this.circleComplete(circle_listener);
    });
  }

  deleteShape(event) {
    // console.log('delete shape');
    if (event) {
      stopLinkBeingFollowed(event)
    }

    if (this.useMultipleShapes) {
      // For WIP applications, clear all shapes
      this.shapes.forEach(shape => {
        shape.mapObject.setVisible(false);
      });
      this.shapes = [];
      this.updateMultipleShapesField();
      this.updateShapesList();
      myMap.showDrawingManager(this.drawingManager);
      this.addListenersToDrawingManager(this.drawingManager);
    } else {
      // Original single shape behavior
      if (this.polygon !== null) {
        this.polygon.setVisible(false);
        this.deletePolygon();
      }
      if (this.circle !== null) {
        this.circle.setVisible(false);
        this.deleteCircle();
      }
      myMap.showDrawingManager(this.drawingManager)
      this.clearCoordinatesDisplay();
      this.addListenersToDrawingManager(this.drawingManager)
    }
  }

  removeShape(event) {
    // console.log('remove individual shape');
    if (event) {
      stopLinkBeingFollowed(event);
    }

    if (this.useMultipleShapes) {
      // Get the shape index from the event target
      const button = event.target;
      const shapeIndex = parseInt(button.dataset.shapeIndex);
      if (!isNaN(shapeIndex)) {
        this.removeShapeFromCollection(shapeIndex);
      }
    }
  }

  zoomMap(event) {
    // console.log('zoom map');
    if (event) { stopLinkBeingFollowed(event); }

    // Handle multiple shapes for WIP applications
    if (this.useMultipleShapes && this.shapes && this.shapes.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      this.shapes.forEach(shape => {
        if (shape.mapObject && shape.mapObject.getBounds) {
          bounds.union(shape.mapObject.getBounds());
        }
      });
      this.map.fitBounds(bounds);
    }
    // Original single shape logic for flight and crane applications
    else if (this.polygon != null) {
      myMap.zoomToShape(this.map, this.polygon);
    } else if (this.circle != null) {
      myMap.zoomToShape(this.map, this.circle);
    }
  }

  async resetMap(event) {
    if (event) { stopLinkBeingFollowed(event); }
    this.closeInfoPanel();
    let mapCentreLat = this.data.get("mapCentreLat");
    let mapCentreLon = this.data.get("mapCentreLon");
    const zoom = parseInt(this.data.get("defaultZoom")) || 12;
    const mapType = resolveMapTypeId(this.data.get("defaultMapType"));
    this.map.setOptions(defaultMapOptions(mapCentreLat, mapCentreLon, zoom, mapType));

    await layerManager.resetAllLayerDefaults(this.element, 'form-map');
  }

  ////////////////////// polygon stuff

  openUploadModal(event) {
    // console.log('Open upload modal');
    stopLinkBeingFollowed(event);
    let uploadModal = new Modal('#upload-kml-file-modal')
    uploadModal.show()
  }

  receiveUploadedKMLPolygon(_this, uploadedAppId, map, drawingManager) {
    _this.deleteShape()
    _this.resetMap()
    let encoded = uploadedAppId.innerText

    if (_this.useMultipleShapes) {
      const mapShape = myMap.drawPolygonFromPath(map, encoded)
      _this.addShapeToCollection('polygon', encoded, mapShape)
      _this.addListenersToPolygon(mapShape)
      myMap.showDrawingManager(drawingManager)
    } else {
      _this.polygonTarget.value = encoded
      _this.polygon = myMap.drawPolygonFromPath(map, encoded)
      zoomToShape(map, _this.polygon)
      _this.updateSingleShapeDisplay('polygon', encoded)
      myMap.hideDrawingManager(drawingManager)
      _this.polygon.setOptions({ editable: true, draggable: true })
      _this.addListenersToPolygon(_this.polygon)
    }
  }

  initialize() {
    let craneZones = []
  }

  async connect() {
    if (!this.hasMapTarget) return;

    let locked = (this.data.get("locked") === 'true');
    let mapCentreLat = this.data.get("mapCentreLat");
    let mapCentreLon = this.data.get("mapCentreLon");
    const zoom = parseInt(this.data.get("defaultZoom")) || 12;
    const mapType = resolveMapTypeId(this.data.get("defaultMapType"));

    let mapOptions = myMap.defaultMapOptions(mapCentreLat, mapCentreLon, zoom, mapType);

    let map = new google.maps.Map(this.mapTarget, mapOptions);
    this.map = map
    await layerManager.initializeLayers(this.map, this);

    myMap.addGetBoundsFunction();

    // Setup info panel for layer feature information
    if (this.hasInfoPanelTarget && this.hasInfoPanelContentTarget) {
      mapInfoPanel.setup(this, this.infoPanelTarget, this.infoPanelContentTarget, this.map);
    }

    let drawingManager = myMap.setUpFormMapDrawingManager(this.map, this.applicationTypeValue);
    this.polygon = null;
    this.circle = null;
    this.drawingManager = drawingManager;

    let polyPath = this.polygonTarget.value;
    let circleCentrePath = this.circleCentreTarget.value;
    let circleRadius = this.circleRadiusTarget.value;

    this.shapes = []; // used to hold multiple shapes in WIP applications

    if (this.useMultipleShapes) {
      this.loadExistingMultipleShapes();
      if (locked) {
        myMap.hideDrawingManager(drawingManager);
      } else {
        myMap.showDrawingManager(drawingManager);
        this.addListenersToDrawingManager(drawingManager);
      }
    } else {
      // Original single shape behaviour for flight and crane applications
      if (polyPath) { // polygon already present
        this.polygon = myMap.drawPolygonFromPath(map, polyPath);
        this.updateSingleShapeDisplay('polygon', polyPath);
        myMap.hideDrawingManager(drawingManager); // hide drawing manager if a polygon is already present
        if (!locked) { // allow editing
          this.polygon.setOptions( { editable: true, draggable: true } );
          this.addListenersToPolygon(this.polygon);
        } else {
          this.addClickForwardingListener(this.polygon);
        }
      }

      if (circleCentrePath) { // circle already present
        this.circle = myMap.drawCircleFromPath(map, circleCentrePath, circleRadius);
        this.updateSingleShapeDisplay('circle', { center: circleCentrePath, radius: circleRadius });
        myMap.hideDrawingManager(drawingManager); // hide drawing manager if a circle is already present
        if (!locked) { // allow editing
          this.circle.setOptions({ editable: true, draggable: true } );
          this.addListenersToCircle(this.circle);
        } else {
          this.addClickForwardingListener(this.circle);
        }
      }

      if (!polyPath && !circleCentrePath && !locked) { // new form, add listeners
        this.addListenersToDrawingManager(drawingManager)
      }
    }

    // add listener for uploaded KML
    const uploadedKMLDiv = document.getElementById('uploaded-kml-div')
    let _this = this
    if (uploadedKMLDiv) {
      let observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          _this.receiveUploadedKMLPolygon(_this, uploadedKMLDiv, map, drawingManager)
        })
      })
      observer.observe(uploadedKMLDiv, { attributes: true, childList: true, characterData: true });
    }
  }

  // Info panel methods
  closeInfoPanel() {
    mapInfoPanel.close();
  }

}
